import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, FolderTree, Loader2, Sparkles } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";
import DokumenTerkaitView from "@/components/taxonomy/DokumenTerkaitView";
import AspectEditorBlock from "@/components/taxonomy/AspectEditorBlock";
import { buildSections } from "@/components/taxonomy/aspectSections";
import type { TaxonomyNode } from "@/components/taxonomy/TaxonomyCascadePicker";

const TAXONOMY_TYPE = "PNBP_DOMAIN";

// Stable empty-array reference. Using a literal `[]` as a useQuery default
// (`data = []`) creates a *new* array on every render when data is undefined,
// which makes any useEffect depending on it re-run every render → infinite
// "Maximum update depth exceeded" loop. Share one frozen instance instead.
const EMPTY_CONTENTS: NodeContent[] = [];

interface Aspect {
  id: string;
  kode: string;
  nama: string;
  urutan: number;
  is_active: boolean;
  parent_id: string | null;
}

interface NodeContent {
  id: string;
  taxonomy_node_id: string;
  aspect_id: string;
  aspect_kode: string;
  aspect_nama: string;
  title: string | null;
  content_html: string | null;
  status: "DRAFT" | "PUBLISHED" | "NONAKTIF";
  urutan: number;
  published_at: string | null;
  updated_at: string | null;
}

interface AIExtractResult {
  status: string;
  generated: string[]; // kode aspek yang terisi
  skipped: string[];
  docs_used: { id: string; judul: string; nomor_dokumen: string | null }[];
  message: string;
}

interface AIExtractProgress {
  done: number;
  total: number;
  current: string;
}

// ── Tree sidebar ──────────────────────────────────────────────────────────────

interface TreeItemProps {
  node: TaxonomyNode;
  selectedId: string | null;
  onSelect: (id: string) => void;
  defaultOpen?: boolean;
}

function TreeItem({ node, selectedId, onSelect, defaultOpen }: TreeItemProps) {
  const [open, setOpen] = useState(!!defaultOpen);
  const hasChildren = !!node.children?.length;
  const active = selectedId === node.id;
  return (
    <div>
      <div
        className={clsx(
          "flex items-start gap-1 rounded px-2 py-1 text-sm cursor-pointer",
          active ? "bg-blue-100 text-blue-800 font-medium" : "hover:bg-gray-100",
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-0.5 w-3.5 shrink-0 text-gray-400"
          aria-label="toggle"
        >
          {hasChildren ? (
            open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : null}
        </button>
        <span
          onClick={() => onSelect(node.id)}
          title={node.kode ? `${node.kode} ${node.nama}` : node.nama}
          className="flex flex-1 gap-1.5 rounded px-1 transition-colors hover:bg-blue-50 hover:text-blue-700"
        >
          {node.kode && (
            <span className="shrink-0 tabular-nums text-gray-400">{node.kode}</span>
          )}
          <span className="break-words">{node.nama}</span>
        </span>
      </div>
      {open && hasChildren && (
        <div className="pl-4 border-l border-gray-100 ml-3">
          {node.children!.map((c) => (
            <TreeItem
              key={c.id}
              node={c}
              selectedId={selectedId}
              onSelect={onSelect}
              defaultOpen={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function TaxonomyContentEditorPage() {
  const queryClient = useQueryClient();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const { data: tree = [], isLoading: treeLoading } = useQuery<TaxonomyNode[]>({
    queryKey: ["taxonomy-tree", TAXONOMY_TYPE],
    queryFn: () => api.get(`/taxonomy/tree?taxonomy_type=${TAXONOMY_TYPE}`),
  });

  const { data: aspects = [] } = useQuery<Aspect[]>({
    queryKey: ["taxonomy-aspects"],
    queryFn: () => api.get("/taxonomy/aspects"),
  });

  // Sort aspects by urutan.
  const sortedAspects = useMemo(
    () => [...aspects].sort((a, b) => a.urutan - b.urutan),
    [aspects],
  );

  // Kelompokkan aspek menjadi section (Definisi+RuangLingkup+Subjek, Objek,
  // Mekanisme+Tarif, Dokumen). Tab di UI = section, bukan per-aspek.
  const sections = useMemo(() => buildSections(sortedAspects), [sortedAspects]);
  const effectiveSectionId = activeSectionId ?? sections[0]?.id ?? null;
  const activeSection = sections.find((s) => s.id === effectiveSectionId) ?? null;

  const { data: contents = EMPTY_CONTENTS } = useQuery<NodeContent[]>({
    queryKey: ["taxonomy-contents", selectedNodeId],
    queryFn: () => api.get(`/taxonomy/${selectedNodeId}/contents`),
    enabled: !!selectedNodeId,
  });

  const [aiResult, setAiResult] = useState<AIExtractResult | null>(null);
  const [aiProgress, setAiProgress] = useState<AIExtractProgress | null>(null);

  const aiExtractMutation = useMutation({
    // Ekstraksi dijalankan sebagai background job di server (LLM bisa sangat
    // lambat). Kita START job lalu POLLING status sampai selesai — tidak ada
    // request HTTP panjang yang bisa timeout. Tiap aspek disimpan inkremental,
    // jadi konten me-refresh bertahap selama proses berjalan.
    mutationFn: async () => {
      if (!selectedNodeId) return null;
      const nodeId = selectedNodeId;
      setAiProgress(null);
      const start = await api.post<{ job_id: string; status: string }>(
        `/taxonomy/${nodeId}/ai-extract`,
        {},
      );
      const jobId = start.job_id;
      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
      const deadline = Date.now() + 30 * 60_000; // batas aman 30 menit
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await sleep(3000);
        const st = await api.get<{
          status: "processing" | "done" | "failed";
          progress?: AIExtractProgress | null;
          result?: AIExtractResult;
          error?: string;
        }>(`/taxonomy/${nodeId}/ai-extract-jobs/${jobId}`);
        if (st.status === "processing") {
          if (st.progress) setAiProgress(st.progress);
          // Draft yang sudah tersimpan tampil bertahap.
          queryClient.invalidateQueries({ queryKey: ["taxonomy-contents", nodeId] });
          if (Date.now() > deadline) throw new Error("Proses ekstraksi melebihi batas waktu (30 menit).");
          continue;
        }
        if (st.status === "failed") throw new Error(st.error || "Ekstraksi gagal.");
        return st.result ?? null; // done
      }
    },
    onSuccess: (res) => {
      setAiResult(res);
      setAiProgress(null);
      queryClient.invalidateQueries({ queryKey: ["taxonomy-contents", selectedNodeId] });
    },
    onError: () => setAiProgress(null),
  });

  // Clear the AI result banner when switching nodes.
  useEffect(() => {
    setAiResult(null);
    setAiProgress(null);
    aiExtractMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  const selectedNodeLabel = useMemo(() => {
    if (!selectedNodeId) return null;
    const find = (nodes: TaxonomyNode[]): TaxonomyNode | undefined => {
      for (const n of nodes) {
        if (n.id === selectedNodeId) return n;
        const inChild = n.children ? find(n.children) : undefined;
        if (inChild) return inChild;
      }
      return undefined;
    };
    return find(tree)?.nama ?? null;
  }, [tree, selectedNodeId]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Konten Aspek Taxonomy</h1>
        <p className="text-sm text-gray-500 mt-1">
          Kelola konten aspek dalam 4 section (Definisi/Ruang Lingkup/Subjek, Objek,
          Mekanisme/Tarif & Perhitungan, Dokumen) untuk setiap node taxonomy.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Tree sidebar */}
        <aside className="rounded-xl border border-gray-200 bg-white p-3 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center gap-2 px-2 py-1 mb-2 border-b border-gray-100">
            <FolderTree className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-700">Pohon Taxonomy</span>
          </div>
          {treeLoading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-xs text-gray-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Memuat…
            </div>
          ) : tree.length === 0 ? (
            <p className="px-2 py-3 text-xs text-gray-500">Belum ada node taxonomy.</p>
          ) : (
            tree.map((root) => (
              <TreeItem
                key={root.id}
                node={root}
                selectedId={selectedNodeId}
                onSelect={setSelectedNodeId}
                defaultOpen
              />
            ))
          )}
        </aside>

        {/* Editor area */}
        <section className="rounded-xl border border-gray-200 bg-white p-5 min-h-[60vh]">
          {!selectedNodeId ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Pilih node taxonomy di samping untuk mengelola konten aspek.
            </div>
          ) : (
            <>
              <div className="mb-4 pb-3 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400">Node</p>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedNodeLabel ?? selectedNodeId}
                    </h2>
                  </div>
                  <button
                    type="button"
                    disabled={aiExtractMutation.isPending}
                    onClick={() => aiExtractMutation.mutate()}
                    title="Isi konten aspek otomatis dari dokumen yang ter-link ke node ini"
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-60"
                  >
                    {aiExtractMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {aiExtractMutation.isPending ? "Meng-generate…" : "Generate dengan AI"}
                  </button>
                </div>

                {aiExtractMutation.isPending && (
                  <div className="mt-2 text-xs text-violet-600">
                    {aiProgress ? (
                      <>
                        <p>
                          Mengekstrak aspek {aiProgress.done}/{aiProgress.total}
                          {aiProgress.current ? ` — ${aiProgress.current}` : ""}…
                        </p>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-violet-100">
                          <div
                            className="h-full rounded-full bg-violet-500 transition-all"
                            style={{
                              width: `${aiProgress.total ? Math.round((aiProgress.done / aiProgress.total) * 100) : 0}%`,
                            }}
                          />
                        </div>
                        <p className="mt-1 text-violet-500">
                          Draft tersimpan bertahap & muncul otomatis. LLM bisa lambat — boleh tinggalkan halaman ini.
                        </p>
                      </>
                    ) : (
                      <p>Memulai job ekstraksi AI di latar belakang…</p>
                    )}
                  </div>
                )}
                {aiResult && !aiExtractMutation.isPending && (
                  <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">
                    <p>{aiResult.message}</p>
                    {aiResult.docs_used.length > 0 && (
                      <p className="mt-1 text-violet-600">
                        Sumber: {aiResult.docs_used.map((d) => d.nomor_dokumen || d.judul).join("; ")}
                      </p>
                    )}
                    {aiResult.skipped.length > 0 && (
                      <p className="mt-1 text-violet-600">
                        Dilewati (sudah PUBLISHED): {aiResult.skipped.join(", ")}
                      </p>
                    )}
                  </div>
                )}
                {aiExtractMutation.isError && (
                  <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {(aiExtractMutation.error as Error)?.message ??
                      "Gagal meng-generate konten dengan AI. Coba lagi."}
                  </p>
                )}
              </div>

              {/* Section tabs */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {sections.map((s) => {
                  const filled = s.aspects.some(
                    (a) =>
                      !s.isDokumen &&
                      contents.some(
                        (c) => c.aspect_kode === a.kode && (c.content_html || c.title),
                      ),
                  );
                  const active = effectiveSectionId === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setActiveSectionId(s.id)}
                      className={clsx(
                        "rounded-full border px-3 py-1 text-xs transition-colors",
                        active
                          ? "border-blue-600 bg-blue-600 text-white"
                          : filled
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                            : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50",
                      )}
                    >
                      {s.label}
                      {filled && !active && <span className="ml-1">●</span>}
                    </button>
                  );
                })}
              </div>

              {/* Section aktif */}
              {activeSection?.isDokumen ? (
                <div>
                  <p className="text-xs text-gray-500 mb-3">
                    Daftar dokumen ini ditarik otomatis dari dokumen yang ter-link PERSIS ke node
                    taxonomy ini (node anak tidak ikut).
                  </p>
                  <DokumenTerkaitView nodeId={selectedNodeId!} />
                </div>
              ) : activeSection ? (
                <div className="space-y-6">
                  {activeSection.aspects.map((a) => (
                    // Key by node+aspect: remount on node switch so each block's
                    // draft re-inits from that node's content (avoids state bleed
                    // when both nodes lack saved content for the aspect).
                    <AspectEditorBlock
                      key={`${selectedNodeId}-${a.id}`}
                      nodeId={selectedNodeId!}
                      aspect={a}
                      content={contents.find((c) => c.aspect_kode === a.kode)}
                      hideHeader={activeSection.aspects.length === 1}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
